import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next";
import useQueryState from "shared/hooks/useQueryState";
import { CompetitionNavigationTab } from "./style";
import { API, Endpoints, Lang, LangObject } from "@brg/api";
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { useDispatch, useSelector } from "react-redux";
import CompetitionAdminSlice, { CompetitionAdminSelector, CompetitionAdminActions } from "shared/reducers/CompetitionAdminSlice";
import { BEST_FOLDER, DEFAULT_FOLDER } from "./const/const";
import EditIcon from "shared/icons/EditIcon";
import { bArray, useModal } from "core";
import { FlexBox } from "@brg/ui";
import { FolderT } from "shared/pages/designer/components/tabs/const";
import { tryParseObject } from "shared/helpers/tryParseObject";
import { HsePolicies } from "shared/constants/HsePolicies";

enum COMPETITION_EDIT_NAVIGATE_TABS {
    main = 'main',
    tags = 'tags',
    jury = 'jury',
    voting = 'voting', 
    nominations = 'nominations',
    notifications = 'notifications',
    applications = 'applications',
    content = 'content',
}


export const useGetCompetitionNavigateTabs = ({ 
    type, 
    disableDefaultFolder, 
    disableFolderIcons, 
    disableFolderActions,
    onFolderClick,
    selectedId  
}: { type: 'admin' | 'editor', disableDefaultFolder?: boolean, disableFolderIcons?: boolean, disableFolderActions?: boolean,
     onFolderClick?: (id: string) => void, selectedId?: string }) => {
    const { t, i18n: { language} } = useTranslation();
    const profile = useProfile();
    const [{ tab: currentTab }, setQueryParams] = useQueryState();
    const dispatch = useDispatch();
    const modal = useModal();
    const { fetchFolders } = CompetitionAdminActions;

    const { folders } = useSelector(CompetitionAdminSelector);

    const isModer = useMemo(() => {
        if (profile) {
          return bProfile.isModer(profile)
        }
      }, [profile])

    const isModerForBestCompetitions = useMemo(() => {
        return profile && profile.rolesList && Array.isArray(profile.rolesList)
        && (new ProfileRole(HsePolicies.access_best_competitions)).containsIn(profile.rolesList)
    } , [profile])

    const folderForBestProjects = useMemo(() => {
        if (isModer || isModerForBestCompetitions) {
            return [BEST_FOLDER];
        }
        return [];
    } , [isModer, isModerForBestCompetitions])

    const defaultFolder = useMemo(() => {
        if (disableDefaultFolder) {
            return []
        }
        return [DEFAULT_FOLDER];
    }, [disableDefaultFolder]);

    useEffect(() => {
        if (profile?.designerId) {
            dispatch(fetchFolders(profile?.designerId));
        }
    }, []);

    useEffect(() => {
        if (!disableDefaultFolder) {
            setQueryParams({ tab: DEFAULT_FOLDER?.id ?? '' }, { replace: true });
        }
    }, []);

    const onClick = (tab: string) => {
        if (onFolderClick) {
            onFolderClick(tab);
            return;
        }
        setQueryParams({ tab });
        dispatch(CompetitionAdminActions.clearSelectedCompetitions());
        dispatch(CompetitionAdminActions.clearCurrentStatusForTab());
    }

    const onEditFolder = (folder: FolderT) => {
       modal.show('competition-edit-folder-modal', {
        folder,
       })
    }

    const parseName = (name: string, lang: string) => {
        let parsedName = ''
        if (typeof name === 'string') {
            const isValidJson = tryParseObject({ str: name || '' })
            if (isValidJson) {
                parsedName = isValidJson as any;
            } else {
                parsedName = name;
            }
        }
        if (Array.isArray(name)) {
            parsedName = name;
        }
        return Lang(parsedName, lang);
    }

    const editButtonVisible = useCallback((folder: FolderT) => {
        if (disableFolderIcons) return false;
        if (folder.id === DEFAULT_FOLDER?.id) return false;
        if (folder.id === BEST_FOLDER.id) return false;
        return true
    }, [disableFolderIcons]);

    const isActive = useCallback((id: string) => {
        if (selectedId) {
            return selectedId === id;
        }
        return currentTab === id;
    }, [selectedId, currentTab]);

    return useMemo(() => {
        if (type === 'admin') {
            return [
                ...defaultFolder, 
                ...folderForBestProjects, 
                ...folders
            ].map(folder => ({
                key: folder.id,
                label: parseName(folder?.name as string, language),
                path: `/${folder.entityType}`,
                component: <CompetitionNavigationTab 
                isActive={isActive(folder.id ?? '')}>
                    <FlexBox direction="row" gx={8}>
                        {!disableFolderIcons && (
                            <div>
                            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.5 0.75H4.47266L6.01172 2.06934L6.22266 2.25H14.5C14.5421 2.25 14.7201 2.29859 14.9365 2.62402C15.138 2.92723 15.25 3.30139 15.25 3.56543V10.4346C15.25 10.9152 14.8845 11.25 14.5 11.25H1.5C1.11555 11.25 0.75 10.9152 0.75 10.4346V1.56543C0.75 1.08479 1.11555 0.75 1.5 0.75Z" stroke="#fff" strokeWidth="1.5"/>
                            </svg></div>
                        )}
                        <div
                            className="folder-name"
                            onClick={() => {
                                onClick(folder.id ?? '')
                            }}
                        >
                            {parseName(folder?.name as string, language)}
                        </div>
                    </FlexBox>
                    {editButtonVisible(folder as FolderT) && (
                        <div 
                            className="edit-icon-wrapper"
                            style={{ opacity: 0}}
                            onClick={() => onEditFolder(folder as FolderT)}
                        >
                            <EditIcon/>
                        </div>
                    )}
                </CompetitionNavigationTab>,
            }))
        }
        return Object.values(COMPETITION_EDIT_NAVIGATE_TABS).map(tab => ({
            key: tab,
            label: tab,
            path: `/${tab}`,
            component: <CompetitionNavigationTab 
            onClick={() => {
                if (!disableFolderActions) {
                    onClick(tab)
                }
            }}
            isActive={currentTab === tab}>{t(`competitions_edit.navigation.${tab}`)}</CompetitionNavigationTab>,
        }))
    }, [type, currentTab, selectedId, isActive, folders, language])
}import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next";
import useQueryState from "shared/hooks/useQueryState";
import { CompetitionNavigationTab } from "./style";
import { API, Endpoints, Lang, LangObject } from "@brg/api";
import { bProfile, ProfileRole, useProfile } from "@brg/auth";
import { useDispatch, useSelector } from "react-redux";
import CompetitionAdminSlice, { CompetitionAdminSelector, CompetitionAdminActions } from "shared/reducers/CompetitionAdminSlice";
import { BEST_FOLDER, DEFAULT_FOLDER } from "./const/const";
import EditIcon from "shared/icons/EditIcon";
import { bArray, useModal } from "core";
import { FlexBox } from "@brg/ui";
import { FolderT } from "shared/pages/designer/components/tabs/const";
import { tryParseObject } from "shared/helpers/tryParseObject";
import { HsePolicies } from "shared/constants/HsePolicies";

enum COMPETITION_EDIT_NAVIGATE_TABS {
    main = 'main',
    tags = 'tags',
    jury = 'jury',
    voting = 'voting', 
    nominations = 'nominations',
    notifications = 'notifications',
    applications = 'applications',
    content = 'content',
}


export const useGetCompetitionNavigateTabs = ({ 
    type, 
    disableDefaultFolder, 
    disableFolderIcons, 
    disableFolderActions,
    onFolderClick,
    selectedId  
}: { type: 'admin' | 'editor', disableDefaultFolder?: boolean, disableFolderIcons?: boolean, disableFolderActions?: boolean,
     onFolderClick?: (id: string) => void, selectedId?: string }) => {
    const { t, i18n: { language} } = useTranslation();
    const profile = useProfile();
    const [{ tab: currentTab }, setQueryParams] = useQueryState();
    const dispatch = useDispatch();
    const modal = useModal();
    const { fetchFolders } = CompetitionAdminActions;

    const { folders } = useSelector(CompetitionAdminSelector);

    const isModer = useMemo(() => {
        if (profile) {
          return bProfile.isModer(profile)
        }
      }, [profile])

    const isModerForBestCompetitions = useMemo(() => {
        return profile && profile.rolesList && Array.isArray(profile.rolesList)
        && (new ProfileRole(HsePolicies.access_best_competitions)).containsIn(profile.rolesList)
    } , [profile])

    const folderForBestProjects = useMemo(() => {
        if (isModer || isModerForBestCompetitions) {
            return [BEST_FOLDER];
        }
        return [];
    } , [isModer, isModerForBestCompetitions])

    const defaultFolder = useMemo(() => {
        if (disableDefaultFolder) {
            return []
        }
        return [DEFAULT_FOLDER];
    }, [disableDefaultFolder]);

    useEffect(() => {
        if (profile?.designerId) {
            dispatch(fetchFolders(profile?.designerId));
        }
    }, []);

    useEffect(() => {
        if (!disableDefaultFolder) {
            setQueryParams({ tab: DEFAULT_FOLDER?.id ?? '' }, { replace: true });
        }
    }, []);

    const onClick = (tab: string) => {
        if (onFolderClick) {
            onFolderClick(tab);
            return;
        }
        setQueryParams({ tab });
        dispatch(CompetitionAdminActions.clearSelectedCompetitions());
        dispatch(CompetitionAdminActions.clearCurrentStatusForTab());
    }

    const onEditFolder = (folder: FolderT) => {
       modal.show('competition-edit-folder-modal', {
        folder,
       })
    }

    const parseName = (name: string, lang: string) => {
        let parsedName = ''
        if (typeof name === 'string') {
            const isValidJson = tryParseObject({ str: name || '' })
            if (isValidJson) {
                parsedName = isValidJson as any;
            } else {
                parsedName = name;
            }
        }
        if (Array.isArray(name)) {
            parsedName = name;
        }
        return Lang(parsedName, lang);
    }

    const editButtonVisible = useCallback((folder: FolderT) => {
        if (disableFolderIcons) return false;
        if (folder.id === DEFAULT_FOLDER?.id) return false;
        if (folder.id === BEST_FOLDER.id) return false;
        return true
    }, [disableFolderIcons]);

    const isActive = useCallback((id: string) => {
        if (selectedId) {
            return selectedId === id;
        }
        return currentTab === id;
    }, [selectedId, currentTab]);

    return useMemo(() => {
        if (type === 'admin') {
            return [
                ...defaultFolder, 
                ...folderForBestProjects, 
                ...folders
            ].map(folder => ({
                key: folder.id,
                label: parseName(folder?.name as string, language),
                path: `/${folder.entityType}`,
                component: <CompetitionNavigationTab 
                isActive={isActive(folder.id ?? '')}>
                    <FlexBox direction="row" gx={8}>
                        {!disableFolderIcons && (
                            <div>
                            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.5 0.75H4.47266L6.01172 2.06934L6.22266 2.25H14.5C14.5421 2.25 14.7201 2.29859 14.9365 2.62402C15.138 2.92723 15.25 3.30139 15.25 3.56543V10.4346C15.25 10.9152 14.8845 11.25 14.5 11.25H1.5C1.11555 11.25 0.75 10.9152 0.75 10.4346V1.56543C0.75 1.08479 1.11555 0.75 1.5 0.75Z" stroke="#fff" strokeWidth="1.5"/>
                            </svg></div>
                        )}
                        <div
                            className="folder-name"
                            onClick={() => {
                                onClick(folder.id ?? '')
                            }}
                        >
                            {parseName(folder?.name as string, language)}
                        </div>
                    </FlexBox>
                    {editButtonVisible(folder as FolderT) && (
                        <div 
                            className="edit-icon-wrapper"
                            style={{ opacity: 0}}
                            onClick={() => onEditFolder(folder as FolderT)}
                        >
                            <EditIcon/>
                        </div>
                    )}
                </CompetitionNavigationTab>,
            }))
        }
        return Object.values(COMPETITION_EDIT_NAVIGATE_TABS).map(tab => ({
            key: tab,
            label: tab,
            path: `/${tab}`,
            component: <CompetitionNavigationTab 
            onClick={() => {
                if (!disableFolderActions) {
                    onClick(tab)
                }
            }}
            isActive={currentTab === tab}>{t(`competitions_edit.navigation.${tab}`)}</CompetitionNavigationTab>,
        }))
    }, [type, currentTab, selectedId, isActive, folders, language])
}